import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useFitbitStatus } from '../../hooks/useFitbitStatus';
import { useDeviceStatus } from '../../hooks/useDeviceStatus';
import { useTheme } from '../../hooks/useTheme';
import apiClient from '../../utils/api';
import API_CONFIG from '../../config/api';
import { healthConnectSupported } from '../../utils/healthConnect';
import { goalText } from '../you/steerText';

// Profile overview (2026-10 restyle): who you are, then every setting as a
// row you can tap. Each row opens the section that owns it. Live values
// where they're cheap; plain words everywhere else.

const Row = ({ to, onClick, label, small, value, dot, danger }) => {
  const cls = `grid w-full grid-cols-[1fr_auto_18px] items-center gap-3.5 border-b border-line px-5 py-4 text-left last:border-b-0 hover:bg-raised ${danger ? 'text-off-strong dark:text-off' : 'text-fg'}`;
  const body = (
    <>
      <span className="min-w-0">
        <span className="block text-[1.05rem] font-medium">{label}</span>
        {small && <span className="mt-0.5 block text-sm font-normal text-fg-3">{small}</span>}
      </span>
      <span className={`inline-flex items-center justify-end gap-2 text-right text-base ${value ? 'text-fg-2' : 'text-fg-3'}`}>
        {dot && <i className={`inline-block h-2 w-2 rounded-full ${dot === 'g' ? 'bg-good-strong dark:bg-good' : dot === 'r' ? 'bg-off-strong dark:bg-off' : 'bg-fg-3'}`} />}
        {value}
      </span>
      <span className="h-2 w-2 justify-self-end -rotate-45 border-b-[1.5px] border-r-[1.5px] border-fg-3" aria-hidden="true" />
    </>
  );
  return to ? <Link to={to} className={cls}>{body}</Link> : <button type="button" onClick={onClick} className={`${cls} bg-transparent`}>{body}</button>;
};

const Section = ({ title, sub, children }) => (
  <section>
    <div className="mb-2.5">
      <h2 className="text-[1.2rem] font-medium tracking-[-0.015em]">{title}</h2>
      {sub && <p className="mt-1 text-sm text-fg-3">{sub}</p>}
    </div>
    <div className="rounded-xl border border-line">{children}</div>
  </section>
);

const agoWord = (iso) => {
  if (!iso) return '';
  const mins = Math.round((Date.now() - new Date(iso)) / 60000);
  if (mins < 60) return `${mins} min ago`;
  if (mins < 60 * 24) return `${Math.round(mins / 60)} h ago`;
  return new Date(iso).toLocaleDateString([], { weekday: 'long' });
};

const ProfileOverview = () => {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { fitbitStatus, loading: fitbitLoading } = useFitbitStatus();
  const { deviceStatus: withings } = useDeviceStatus('withings');
  const { deviceStatus: oura } = useDeviceStatus('oura');
  const [targets, setTargets] = useState([]);
  const [interview, setInterview] = useState(null);

  useEffect(() => {
    apiClient.get('/api/twin/targets').then((r) => setTargets((r.data || []).filter((t) => t.pinned))).catch(() => {});
    apiClient.get('/api/interview/today').then((r) => setInterview(r.data)).catch(() => {});
  }, []);

  const name = user?.display_name || user?.name || (user?.email || '').split('@')[0];
  const since = user?.created_at ? new Date(user.created_at).toLocaleDateString([], { month: 'long', year: 'numeric' }) : '';
  const avatar = user?.profile_picture ? `${API_CONFIG.UPLOADS_BASE_URL}/profile_pictures/${user.profile_picture}` : null;
  const setup = interview?.setup;
  const routines = interview?.routines?.length || 0;
  const perDay = interview?.limit;

  const deviceValue = (st, loading) => (loading ? '…' : st?.connected ? (st.last_sync_at || st.last_synced_at ? `Synced ${agoWord(st.last_sync_at || st.last_synced_at)}` : 'Connected') : 'Not connected');

  return (
    <>
      <div className="flex flex-wrap items-center gap-[18px] rounded-[18px] border border-line bg-raised px-[22px] py-5">
        <div className="grid h-16 w-16 flex-none place-items-center overflow-hidden rounded-full border border-line-strong bg-elev text-[1.5rem] font-medium">
          {avatar ? <img src={avatar} alt="" className="h-full w-full object-cover" /> : (name || '?').charAt(0).toUpperCase()}
        </div>
        <div className="min-w-0">
          <div className="text-[1.35rem] font-medium tracking-[-0.015em]">{name}</div>
          <div className="mt-0.5 text-fg-3">{user?.email}{since ? ` · Member since ${since}` : ''}</div>
        </div>
        <Link to="/me/profile" className="ml-auto text-sm font-medium text-fg-2 hover:text-fg">Edit photo →</Link>
      </div>

      <div className="mt-7 grid gap-x-6 gap-y-7 md:grid-cols-2">
        <Section title="Devices">
          <Row to="/me/devices" label="Fitbit" small="Sleep, steps, heart rate, blood oxygen" value={deviceValue(fitbitStatus, fitbitLoading)} dot={fitbitStatus?.connected ? 'g' : null} />
          <Row to="/me/devices" label="Withings scale" small="Weight" value={deviceValue(withings)} dot={withings?.connected ? 'g' : null} />
          <Row to="/me/devices" label="Oura ring" small="Sleep, readiness" value={deviceValue(oura)} dot={oura?.connected ? 'g' : null} />
          {healthConnectSupported() && <Row to="/me/devices" label="Health Connect" small="Android phone" value="" />}
          <Row to="/me/devices" label="+ Add a device" small="Apple Watch, Garmin, Google Fit" />
        </Section>

        <Section title="About you" sub="Sets what counts as usual for you.">
          <Row to="/me/health" label="Age, sex, height" />
          <Row to="/me/health" label="Health notes" small="Conditions and medications" />
          <Row to="/me/traits" label="A few more questions" small="Caffeine, alcohol, work hours, and so on"
            value={setup ? `${setup.answered} of ${setup.total} answered` : ''} />
        </Section>

        <Section title="Numbers you steer" sub="What shows on the You page.">
          {targets.map((t) => (
            <Row key={t.metric_key} to="/you" label={t.label} value={goalText(t) ? `Goal ${goalText(t)}` : ''} />
          ))}
          <Row to="/you" label="+ Add a number" />
        </Section>

        <Section title="Daily check-in">
          <Row to="/me/insights" label="Questions we ask" small="How you felt, alcohol, pain, and more" value={perDay ? `Up to ${perDay} a day` : ''} />
          <Row to="/me/checklist" label="One-tap items" small="Routines you log with one tap" value={routines ? String(routines) : 'None yet'} />
          <Row to="/me/reminders" label="Reminders" />
        </Section>

        <Section title="Look and feel">
          <Row onClick={toggleTheme} label="Theme" value={theme === 'dark' ? 'Dark' : 'Light'} />
          <Row to="/me/preferences" label="Units and time zone" value={user?.weight_unit === 'lb' ? 'Pounds' : user?.weight_unit === 'kg' ? 'Kilos' : ''} />
        </Section>

        <Section title="Account">
          <Row to="/me/account" label="Email" value={user?.email} />
          <Row to="/me/account" label="Password" value={user?.has_password ? 'Change' : 'Google sign-in'} />
          <Row to="/me/account" label="Download my data" small="Everything, as a file" />
          <Row onClick={logout} label="Log out" />
          <Row to="/me/account" label="Delete my account" danger />
        </Section>
      </div>
    </>
  );
};

export default ProfileOverview;
